import { useMemo, useState } from 'react'
import { TabsContent } from '@design-system/components/tabs'
import { Skeleton } from '@design-system/components/skeleton'
import { ROUTES } from '@/routes/routes'
import { TooltipProvider } from '@/components/ui/tooltip'
import { type SubscriptionTier, type UserProfile } from '@/lib/api'
import { OverageControls } from './OverageControls'
import { PlanCardsRow } from './PlanCardsRow'
import { PlanSummaryCard } from './PlanSummaryCard'
import { PlanViolationBanner } from './PlanViolationBanner'
import { TeamUsageSection } from './TeamUsageSection'
import { UsageLogTable } from './UsageLogTable'
import { useCreditHistory, useMemberUsage } from './useBillingData'
import { getOverageRateForPlan } from './planConfig'
import { LockedFreeGate } from '@/components/billing/LockedFreeGate'
import { CreditScenarioModal } from './CreditScenarioModal'
import { HowCreditsWorkDialog } from './HowCreditsWorkDialog'
import { Calculator } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface BillingTabProps {
  profile: UserProfile | null
  onProfileUpdate: () => void
  onOpenPortal?: () => void
  openingPortal?: boolean
}

function isAdminRole(role: string | undefined | null): boolean {
  return role === 'owner' || role === 'admin'
}

function isOwnerRole(role: string | undefined | null): boolean {
  return role === 'owner'
}

export function BillingTab(props: BillingTabProps) {
  const { profile, onProfileUpdate } = props
  const { t } = useTranslation('app')

  const tier = (profile?.subscriptionTier ?? null) as SubscriptionTier | null
  const orgId = profile?.organizationToken
  const isOwner = isOwnerRole(profile?.orgRole) || (profile?.creditInfo?.isOrgOwner ?? false)
  const isAdmin = isAdminRole(profile?.orgRole) || (profile?.creditInfo?.isOrgOwner ?? false)
  const interval = profile?.billingInterval ?? null
  const hasActiveSubscription = profile?.hasActiveSubscription ?? false

  // Hooks must be called unconditionally, before any early return.
  const memberUsage = useMemberUsage(isAdmin && profile ? orgId : undefined)

  // Fetch the largest window the API allows in one shot so the table can
  // group consecutive runs (chat sessions, etc.) without page boundaries
  // splitting them. Pagination over groups happens client-side inside
  // <UsageLogTable />.
  const USAGE_FETCH_LIMIT = 100
  const creditHistory = useCreditHistory(!!profile, {
    limit: USAGE_FETCH_LIMIT,
    offset: 0,
  })

  const members = memberUsage.data ?? []
  const overageRateCents = getOverageRateForPlan(tier, interval)

  const userName = profile?.name || profile?.email || t('billing.you')

  const [scenarioOpen, setScenarioOpen] = useState(false)
  const [creditsOpen, setCreditsOpen] = useState(false)

  const planViolation = profile?.planViolation === true

  const maxSeats = useMemo(() => {
    // Approximation while there is no plan config endpoint; mirrors backend HARDCODED_DEFAULTS.
    if (tier === 'pro' || tier === 'enterprise') return 9999
    return 1
  }, [tier])

  // Skeleton-gate: render after all hooks have been called.
  if (!profile) {
    return (
      <TabsContent value="billing" className="space-y-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="flex flex-col gap-4 lg:col-span-5">
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
          <div className="lg:col-span-7">
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        </div>
        <Skeleton className="h-64 w-full rounded-2xl" />
      </TabsContent>
    )
  }

  return (
    <TabsContent value="billing" className="space-y-6">
      {/* Single Provider for the whole tab — Radix expects one near the root */}
      <TooltipProvider delayDuration={150}>
      {tier === 'locked_free' && (
        <LockedFreeGate
          inline
          action={t('billing.gatedAction')}
          ctaLabel={t('billing.upgrade')}
        />
      )}

      <PlanViolationBanner
        show={planViolation}
        isAdmin={isAdmin}
        maxSeats={maxSeats}
        memberCount={members.length || 1}
        showUpgradeCta={hasActiveSubscription}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="flex flex-col gap-3 lg:col-span-5">
          <div className="flex items-center gap-4 min-h-[36px]">
            <button
              type="button"
              onClick={() => setScenarioOpen(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-2 hover:underline transition-colors"
              data-testid="credit-scenario-trigger"
            >
              <Calculator className="h-3.5 w-3.5" />
              {t('billing.estimateUsage')}
            </button>
            <button
              type="button"
              onClick={() => setCreditsOpen(true)}
              className="text-sm font-medium text-foreground underline-offset-2 hover:underline transition-colors"
              data-testid="billing-how-credits-work-trigger"
            >
              {t('billing.plans.howCreditsWork')}
            </button>
          </div>

          <PlanSummaryCard
            tier={tier as SubscriptionTier}
            creditInfo={profile.creditInfo}
            isPoolBilling={profile.creditInfo?.isPoolBilling ?? false}
            daysUntilReset={profile.daysUntilReset ?? 0}
            renewsAt={profile.renewsAt ?? null}
            trialEndsAt={profile.trialEndsAt ?? null}
            endsAt={profile.endsAt ?? null}
            lsStatus={profile.lsStatus ?? null}
            overageRateCents={overageRateCents}
          />

          {isAdmin && orgId && (
            <OverageControls
              orgId={orgId}
              allowOverage={profile.creditInfo?.allowOverage ?? false}
              defaultOverageLimitCents={profile.creditInfo?.overageLimitCents ?? null}
              onChange={onProfileUpdate}
            />
          )}
        </div>

        <div className="lg:col-span-7">
          <PlanCardsRow
            currentTier={tier as SubscriptionTier}
            hasActiveSubscription={hasActiveSubscription}
            currentInterval={interval}
            memberCount={members.length}
            isOwner={isOwner}
          />
        </div>
      </div>

      {creditHistory.error ? (
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-muted-foreground">
          {t('billing.loadUsageFailed')}
        </div>
      ) : (
        <UsageLogTable
          transactions={creditHistory.data?.items ?? []}
          loading={creditHistory.isLoading}
          viewerFallbackDisplay={userName}
          currentUserId={profile.id}
          costPerCreditCents={overageRateCents}
          showCost={(profile?.creditInfo?.overageUsed ?? 0) > 0}
        />
      )}

      {isAdmin && (
        memberUsage.error ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-muted-foreground">
            {t('billing.loadTeamFailed')}
          </div>
        ) : (
          <TeamUsageSection
            members={members}
            loading={memberUsage.isLoading}
            manageTeamHref={ROUTES.settingsTab('account')}
            overageRateCents={overageRateCents}
          />
        )
      )}
      <CreditScenarioModal
        open={scenarioOpen}
        onOpenChange={setScenarioOpen}
        interval={interval}
      />
      <HowCreditsWorkDialog open={creditsOpen} onOpenChange={setCreditsOpen} />
      </TooltipProvider>
    </TabsContent>
  )
}

export default BillingTab
